// In-canvas text editing overlay for the Type tool.
// Replaces window.prompt() with a proper textarea overlay
// positioned at the text layer's location on the canvas.

import { docToScreen } from "../../core/render/Viewport";
import type { Viewport } from "../../core/render/Viewport";
import type { TypeLayer } from "../../core/document/TypeLayer";

export interface TextEditorOptions {
  viewport: Viewport;
  layer: TypeLayer;
  canvasRect: DOMRect;
  initialText?: string;
  /** When true, all text is selected immediately after the editor opens. */
  selectAll?: boolean;
  commit: (text: string) => void;
  cancel: () => void;
}

export class TextEditorOverlay {
  private el: HTMLDivElement | null = null;
  private textarea: HTMLTextAreaElement | null = null;
  private committed = false;
  private commitFn: ((text: string) => void) | null = null;
  private cancelFn: (() => void) | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  /** id of the TypeLayer currently open for editing, or null when closed. */
  private editingLayerId: string | null = null;
  /**
   * The layer + canvasRect the editor was last opened/repositioned for —
   * retained so reposition() can be called with just the live viewport
   * (#i195), and so open() can size paragraph text boxes to match
   * Canvas2DRenderer's rendering (#i193).
   */
  private currentLayer: TypeLayer | null = null;
  private currentCanvasRect: DOMRect | null = null;

  open(opts: TextEditorOptions): void {
    this.close();
    this.committed = false;
    this.commitFn = opts.commit;
    this.cancelFn = opts.cancel;
    this.editingLayerId = opts.layer.id;
    this.currentLayer = opts.layer;
    this.currentCanvasRect = opts.canvasRect;

    const isParagraph = !!opts.layer.textBounds;
    // Paragraph text boxes are anchored at their own top-left (layer.x/y +
    // textBounds.x/y), matching Canvas2DRenderer's getTypeLayerBounds/paint
    // convention. Point text is anchored at layer.x/y directly.
    const boxOffsetX = opts.layer.textBounds?.x ?? 0;
    const boxOffsetY = opts.layer.textBounds?.y ?? 0;
    const screen = docToScreen(opts.viewport, opts.layer.x + boxOffsetX, opts.layer.y + boxOffsetY);
    const left = screen.x + opts.canvasRect.left;
    const top = screen.y + opts.canvasRect.top;

    const color = opts.layer.color;
    const cssColor = `rgb(${color.r}, ${color.g}, ${color.b})`;

    const container = document.createElement("div");
    container.className = "text-editor-overlay";
    container.style.position = "fixed";
    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
    container.style.zIndex = "100";
    container.style.minWidth = "120px";
    container.style.minHeight = "1.4em";
    container.style.outline = "1px dashed rgba(255,255,255,0.5)";
    container.style.background = "transparent";
    container.style.padding = "0";
    container.style.margin = "0";
    container.style.lineHeight = "1.2";

    // Point text (no textBounds): anchor the *text*, not the box, at
    // layer.x/y — matching Canvas2D's ctx.textAlign semantics where center-
    // /right-aligned text is centered/ends on the anchor point. Shifting the
    // whole (auto-width) box via a CSS transform achieves this without
    // needing to measure the rendered text width in JS (#i194).
    if (!isParagraph) {
      if (opts.layer.textAlign === "center") container.style.transform = "translateX(-50%)";
      else if (opts.layer.textAlign === "right") container.style.transform = "translateX(-100%)";
    }

    const textarea = document.createElement("textarea");
    textarea.className = "text-editor-overlay__textarea";
    textarea.value = opts.initialText ?? opts.layer.text;
    textarea.style.fontFamily = opts.layer.fontFamily;
    textarea.style.fontSize = `${opts.layer.fontSize * opts.viewport.zoom}px`;
    textarea.style.fontWeight = `${opts.layer.fontWeight}`;
    textarea.style.fontStyle = opts.layer.fontStyle;
    textarea.style.color = cssColor;
    textarea.style.textAlign = opts.layer.textAlign === "justify" ? "left" : opts.layer.textAlign;
    textarea.style.background = "transparent";
    textarea.style.border = "none";
    textarea.style.outline = "none";
    textarea.style.resize = "none";
    textarea.style.padding = "0";
    textarea.style.margin = "0";
    textarea.style.minHeight = "1.4em";
    textarea.style.lineHeight = "1.2";
    textarea.style.overflow = "hidden";
    textarea.style.whiteSpace = "pre-wrap";
    textarea.style.wordBreak = "break-word";
    textarea.style.caretColor = cssColor;

    if (isParagraph) {
      // Paragraph text: size/wrap the editor to the box, same as the final
      // render, instead of free-growing (#i193).
      const tb = opts.layer.textBounds!;
      const w = `${tb.w * opts.viewport.zoom}px`;
      const h = `${tb.h * opts.viewport.zoom}px`;
      container.style.width = w;
      container.style.height = h;
      textarea.style.width = w;
      textarea.style.height = h;
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.width = "400px";
    }

    container.appendChild(textarea);
    document.body.appendChild(container);

    this.el = container;
    this.textarea = textarea;

    // Focus and optionally select all text.
    textarea.focus();
    if (opts.selectAll !== false) {
      // Default: select all (Photoshop behavior for both new text and
      // thumbnail re-entry).
      textarea.select();
    } else {
      // Move caret to end so re-entry without select-all lands at the end.
      const len = textarea.value.length;
      textarea.setSelectionRange(len, len);
    }

    // Auto-resize textarea to fit content — point text only. Paragraph text
    // boxes keep their fixed size (matching the final render) and scroll
    // internally instead of growing (#i193).
    const autoResize = () => {
      if (isParagraph) return;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };
    textarea.addEventListener("input", autoResize);
    autoResize();

    // Keyboard handling (Photoshop parity):
    //   Numpad Enter or Ctrl+Enter  → commit the text editing session.
    //   Main keyboard Enter         → insert a newline (browser default).
    //   Escape                      → cancel (restore original text).
    textarea.addEventListener("keydown", (e) => {
      const isCommitKey =
        e.code === "NumpadEnter" ||
        (e.key === "Enter" && (e.ctrlKey || e.metaKey));
      if (isCommitKey) {
        e.preventDefault();
        this.doCommit(textarea.value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.doCancel();
      }
      // Main keyboard Enter with no modifiers falls through to the browser
      // default, which inserts a newline into the textarea.
    });

    // Click outside commits.
    const onDocClick = (e: MouseEvent) => {
      if (!container.contains(e.target as Node)) {
        this.doCommit(textarea.value);
      }
    };
    this.outsideClickHandler = onDocClick;
    // Delay to avoid catching the initial click that opened the editor.
    requestAnimationFrame(() => {
      if (this.outsideClickHandler) {
        document.addEventListener("mousedown", this.outsideClickHandler);
      }
    });
  }

  private doCommit(text: string): void {
    if (this.committed) return;
    this.committed = true;
    const fn = this.commitFn;
    this.close();
    fn?.(text);
  }

  private doCancel(): void {
    if (this.committed) return;
    this.committed = true;
    const fn = this.cancelFn;
    this.close();
    fn?.();
  }

  close(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener("mousedown", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.el && this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    this.el = null;
    this.textarea = null;
    this.commitFn = null;
    this.cancelFn = null;
    this.editingLayerId = null;
    this.currentLayer = null;
    this.currentCanvasRect = null;
  }

  isActive(): boolean {
    return this.el !== null && !this.committed;
  }

  /**
   * Re-position (and re-scale) the overlay in place after the viewport
   * (pan/zoom) or canvas layout changes while an editing session is open.
   * Host code — e.g. the canvas wheel handler — should call this whenever
   * the viewport changes and isActive() is true, so the overlay tracks the
   * underlying layer instead of visually drifting out of sync with the
   * canvas (#i195). No-op when the editor isn't open.
   */
  reposition(viewport: Viewport, canvasRect: DOMRect): void {
    if (!this.el || !this.textarea || this.committed || !this.currentLayer) return;
    this.currentCanvasRect = canvasRect;
    const layer = this.currentLayer;

    const boxOffsetX = layer.textBounds?.x ?? 0;
    const boxOffsetY = layer.textBounds?.y ?? 0;
    const screen = docToScreen(viewport, layer.x + boxOffsetX, layer.y + boxOffsetY);
    const left = screen.x + canvasRect.left;
    const top = screen.y + canvasRect.top;
    this.el.style.left = `${left}px`;
    this.el.style.top = `${top}px`;

    if (!layer.textBounds) {
      this.el.style.transform =
        layer.textAlign === "center" ? "translateX(-50%)" : layer.textAlign === "right" ? "translateX(-100%)" : "";
    }

    this.textarea.style.fontSize = `${layer.fontSize * viewport.zoom}px`;

    if (layer.textBounds) {
      const w = `${layer.textBounds.w * viewport.zoom}px`;
      const h = `${layer.textBounds.h * viewport.zoom}px`;
      this.el.style.width = w;
      this.el.style.height = h;
      this.textarea.style.width = w;
      this.textarea.style.height = h;
    }
  }

  /**
   * Return the id of the TypeLayer currently open for editing, or null when
   * the editor is closed. Used by the Character panel to decide whether a
   * property change should apply to just the selected character range
   * (via TypeLayer's applyFontSizeToRuns) or to the whole layer (audit i17).
   */
  getEditingLayerId(): string | null {
    return this.committed ? null : this.editingLayerId;
  }

  /**
   * Programmatically commit the current editor contents.
   * Called by the host when the active tool changes while the editor is open
   * (tool-switch commit, Photoshop parity).
   */
  commitNow(): void {
    if (this.textarea && !this.committed) {
      this.doCommit(this.textarea.value);
    }
  }

  /**
   * Return the current textarea selection range, or null if the editor is
   * not open. Used by CharacterPanel to apply font-size to the selected range.
   */
  getSelection(): { start: number; end: number } | null {
    if (!this.textarea || this.committed) return null;
    return {
      start: this.textarea.selectionStart,
      end: this.textarea.selectionEnd,
    };
  }

  /** Return the current textarea value, or null when editor is closed. */
  getCurrentText(): string | null {
    if (!this.textarea || this.committed) return null;
    return this.textarea.value;
  }
}
